/**
 * Verse urenbalans per blok — de tegenhanger van samenvattingPerBlok.
 *
 * samenvattingPerBlok wordt één keer bij het genereren gevuld — het `samenvatting`-object
 * dat draaiMaandrooster teruggeeft — en daarna nooit meer herrekend. Verandert een contract
 * of komt er verlof bij, dan blijft de tabel de oude cijfers tonen: op de live-data van 9 aug
 * stond Haruun op target 144 terwijl zijn contract al 40u/week was (= 160 over een blok van 4).
 *
 * Dit bestand rekent dezelfde vier velden opnieuw uit, uit de bronnen die WEL live zijn:
 * regels.medewerkers (contract), afwezigheid, vakanties en de urenTeller van de weken zelf.
 *
 * De formules zijn LETTERLIJK die van de engine — de afwezigheids-credit en de lus die
 * `samenvatting` vult, aan het eind van draaiMaandrooster; niet een eigen benadering:
 *   target     = aantalWeken * mw.contract_uren
 *   vasteUren  = som van de HANDMATIG INGEVULDE uren (verlof, ziekte) van de dagen in dit blok
 *   blokGap    = Math.max(0, target - gepland - vasteUren)
 *   afwUren    = vasteUren + Math.max(0, Math.min(blokGap, 8 * restTypes.length))
 *   afwPerType = vasteUren naar de bak van het type van hun dag, daarna de rest
 *                chronologisch splitsen, 8u per keer, gecapt op de credit
 *   afwijking  = gepland + afwUren - target
 * en wbTypeLijst wordt gevuld zoals verwerkUren dat doet: per week oplopend, eerst de
 * ongedekte would-be-werkdagen in ALLE_DAGEN-volgorde, daarna de weektotalen per type.
 *
 * HANDMATIG INGEVULDE UREN (data/verlofuren.js). Een verlof- of ziekterecord mag een veld
 * `uren` dragen: het totaal uit Numbers, daar verdeeld over de HELE periode. Per week komt het
 * hier aan als { naam: { perType, dagen } }: weektotaal per type, en welke dagen gedekt zijn.
 * Dat weektotaal telt ZONDER aftopping — Numbers heeft dat al gedaan — en een gedekte dag
 * krijgt geen 8u meer. Alle andere dagen ('anders', en verlof of ziekte zonder ingevuld getal)
 * houden het oude pad: 8u per would-be-werkdag, afgetopt op wat er van het blok-gat over is.
 *
 * ÉÉN AFWIJKING van de engine, bewust: `gepland` gaat hier ook de SPOOKAFTREK af (8u per dag
 * waarop iemand in een roostercel staat terwijl hij die dag afwezig is). De engine kan dat niet
 * doen — zijn urenTeller wordt bij het genereren gevuld en het verlof komt er daarna pas bij.
 * Zie het commentaarblok bij afwezigCelUren in versBalansVoorBlok.
 *
 * Bewezen gelijk aan de engine op de live-export van 9 aug: 50 van de 52 vergelijkingen
 * identiek, de twee afwijkingen allebei Haruun en allebei zijn contractwijziging
 * (client/test/diag/balans-vers-vs-bevroren.mjs, en de herhaling daarvan in
 * client/test/diag/balans-lib-vs-diag.mjs die déze functie toetst).
 *
 * De engine wordt hier alleen GELEZEN. Importeren uit engine/ mag vanuit lib/ —
 * lib/fte.js:25 doet het al. Andersom niet: engine/ mag nooit uit lib/ importeren,
 * want de deploy-zip bevat alleen engine/ en data/ (zie lib/rollen.js:14-15).
 */

import { wouldBeWerkdag, schoolweekType } from '../engine/rooster-engine.js';
import { bouwAfwezigheidMap, bouwAfwezigheidTypeMap, ALLE_DAGEN } from '../data/afwezigheid.js';
import { bouwAfwezigheidUrenMap } from '../data/verlofuren.js';
import { vakantieDagenInWeek } from '../data/vakanties.js';

const UUR_PER_DIENST = 8;

/**
 * Het weekobject van (isoJaar, weeknummer), of null.
 *
 * Twee bronnen, in volgorde van betrouwbaarheid — dezelfde rangorde als
 * vorigeVensterWeek in Weekrooster.jsx voor de reeks-teller:
 *   1. vastgelegdeWeken[`${isoJaar}-${weeknummer}`].week — de bevroren kopie die de server
 *      serveert. Op de live-export van 9 aug is die voor alle negen vastgelegde weken
 *      identiek aan de maandroosters-variant, urenTeller inbegrepen.
 *   2. een scan over ALLE maandroosters. Nodig omdat een blok over twee maanden kan lopen:
 *      blok 8 is week 29-32, waarvan 29-31 in maandrooster 2026-7 staan en 32 in 2026-8.
 *
 * De matchvorm is letterlijk overgenomen uit lib/divergentie.js:41-48, inclusief de
 * `?? null` aan beide kanten zodat undefined en null op elkaar matchen.
 *
 * Er is nooit meer dan één treffer: een maand bestaat uit de ISO-weken waarvan de MAANDAG
 * in die maand valt (data/maand.js:4, :39-52), en elke maandag valt in precies één maand.
 * Op de live-export komt geen enkele week in twee maandroosters voor. Daarom mag de eerste
 * treffer meteen terug — er valt niets te kiezen.
 */
/**
 * Staat `naam` op `dag` in een dienstcel van dit weekrooster?
 *
 * Zelfde vorm en zelfde volgorde-onafhankelijke lus als getCel (roostercodes.js:423-433) en
 * vindPlaatsing (Weekrooster.jsx:148): gemeente → dienst → dag → namenlijst. Geen eigen
 * cel-logica: het is precies de structuur die analyseerWeek aftelt als hij 8u optelt
 * (analyseer-week.js:80-89).
 *
 * Geeft `true` bij de EERSTE treffer, dus twee cellen op één dag tellen als één dag. Dat is de
 * afspraak van deze aftrek — 8u per dag — en niet wat de urenTeller doet: die telt per
 * plaatsing, dus twee cellen zouden daar 16u zijn. Op de live-data van 25 aug staat niemand
 * ooit in twee cellen op dezelfde dag (gemeten in test/diag/spookaftrek-blok9-10.mjs), dus de
 * twee lezingen vallen daar samen.
 */
function staatInRoostercel(rooster, naam, dag) {
  if (!rooster) return false;
  for (const gemKey of Object.keys(rooster)) {
    for (const dienstId of Object.keys(rooster[gemKey] || {})) {
      if ((rooster[gemKey][dienstId]?.[dag] || []).includes(naam)) return true;
    }
  }
  return false;
}

export function zoekWeekObject(isoJaar, weeknummer, maandroosters, vastgelegdeWeken) {
  const vast = vastgelegdeWeken?.[`${isoJaar}-${weeknummer}`]?.week;
  if (vast) return vast;

  if (!maandroosters || typeof maandroosters !== 'object') return null;
  for (const mr of Object.values(maandroosters)) {
    for (const w of (mr?.weken || [])) {
      if (w?.weeknummer === weeknummer && (w?.isoJaar ?? null) === (isoJaar ?? null)) return w;
    }
  }
  return null;
}

/**
 * De vier balansvelden voor één medewerker over één blok, vers berekend.
 *
 * @param {object}   o
 * @param {Array}    o.blokWeken       weeknummers van het blok. Een element mag ook een
 *                                     weekobject zijn ({ weeknummer, isoJaar }) — de
 *                                     week-modus geeft de vensterweken zo door.
 * @param {number}   o.isoJaar         ISO-jaar van het blok (per week overrulebaar, zie boven)
 * @param {string}   o.mwKey           sleutel in regels.medewerkers — urenTeller is hierop gesleuteld
 * @param {object}   o.mw              het medewerker-object — rooster-cellen staan op mw.naam
 * @param {object}   o.maandroosters   store.maandroosters
 * @param {object}   o.vastgelegdeWeken store.vastgelegdeWeken
 * @param {Array}    o.afwezigheid     de HUIDIGE lijst, niet gegenereerdMetAfwezigheid
 * @param {object}   o.regels          volledige regelset (wouldBeWerkdag heeft hem nodig voor
 *                                     de vast_rotatiepatroon-tak, rooster-engine.js:167)
 * @param {Array}    o.vakanties       de LIJST met periodes, niet één Set
 * @param {number}   o.markeringDelta  uren-effect van handmatige celmarkeringen voor DEZE
 *                                     medewerker (berekenMarkeringDelta in Weekrooster.jsx)
 * @param {number}   o.bevrorenCumUren terugval voor Gepland als een blokweek ontbreekt
 * @returns {{target:number, gepland:number, afwUren:number, afwPerType:object,
 *            afwijking:number, ontbrekendeWeken:number[], afwezigCelUren:number}}
 *          afwezigCelUren = de spookaftrek die al IN gepland verwerkt is (zie daar);
 *          gepland zonder die correctie is `gepland + afwezigCelUren`.
 */
export function versBalansVoorBlok({
  blokWeken,
  isoJaar,
  mwKey,
  mw,
  maandroosters,
  vastgelegdeWeken,
  afwezigheid,
  regels,
  vakanties,
  markeringDelta = 0,
  bevrorenCumUren,
}) {
  // Een element mag een nummer of een weekobject zijn; het isoJaar van het weekobject wint,
  // want een maand kan twee ISO-jaren mengen (december 2025 was 49,50,51,52,1).
  const weken = (blokWeken || []).map((w) => (
    typeof w === 'object' && w !== null
      ? { weeknummer: w.weeknummer, isoJaar: w.isoJaar ?? isoJaar }
      : { weeknummer: w, isoJaar }
  ));

  const target = weken.length * (mw?.contract_uren || 0);

  // ── Gepland ────────────────────────────────────────────────────────────────
  // Som van week.urenTeller[mwKey] over de blokweken. Ontbreekt er een week, dan mag die
  // NOOIT stil als 0 uur meetellen: dat zou de persoon een tekort van een hele week geven.
  // Een blokweek is vindbaar precies wanneer de maand van zijn maandag gegenereerd is; op
  // de live-export ontbreken zo blok 3 week 9 (februari) en blok 13 week 50-53 (december).
  // In dat geval valt Gepland terug op het bevroren cumUren en meldt ontbrekendeWeken het.
  const ontbrekendeWeken = [];
  let somUrenTeller = 0;
  const gevonden = weken.map((w) => {
    const weekObj = zoekWeekObject(w.isoJaar, w.weeknummer, maandroosters, vastgelegdeWeken);
    if (!weekObj) {
      ontbrekendeWeken.push(w.weeknummer);
      return null;
    }
    somUrenTeller += (weekObj.urenTeller && weekObj.urenTeller[mwKey]) || 0;
    return { ...w, weekObj };
  });

  const basis = ontbrekendeWeken.length === 0
    ? somUrenTeller
    : (typeof bevrorenCumUren === 'number' ? bevrorenCumUren : 0);

  // ── Would-be-werkdagen afwezig + spookdagen, chronologisch ────────────────
  // Zelfde volgorde als de engine: weken oplopend, binnen een week ALLE_DAGEN-volgorde.
  // Ontbrekende weken slaan we over — zonder weekobject is er geen betrouwbaar oordeel,
  // en het weekobject is hier alleen als bewijs-van-bestaan nodig, niet als invoer.
  //
  // In dezelfde lus wordt de SPOOKAFTREK geteld, want die heeft precies dezelfde drie dingen
  // nodig die hier al klaarliggen: het weekobject, de afwezigheidsdagen van deze medewerker in
  // deze week, en zijn naam. Zie het commentaarblok bij afwezigCelUren hieronder.
  const wbTypeLijst = [];
  let afwezigCelDagen = 0;
  for (const w of gevonden) {
    if (!w) continue;
    const weekType = schoolweekType(w.weeknummer);
    const vakDagen = new Set(vakantieDagenInWeek(w.weeknummer, w.isoJaar, vakanties));
    const afwDagen = bouwAfwezigheidMap(afwezigheid || [], w.weeknummer, w.isoJaar)[mw.naam] || null;
    if (!afwDagen) continue;
    const alleAfwDagen = [...afwDagen];
    const typeVanDag = bouwAfwezigheidTypeMap(afwezigheid || [], w.weeknummer, w.isoJaar)[mw.naam] || {};
    // Handmatig ingevulde uren voor DEZE week, als { perType, dagen }. De verdeling gaat over
    // de HELE periode van het record (data/verlofuren.js), dus ook als die over een week- of
    // blokgrens loopt; hier wordt alleen het deel van DEZE week opgezocht. De type-controle
    // staat in die laag, niet hier.
    const wkUren = bouwAfwezigheidUrenMap(afwezigheid || [], w.weeknummer, w.isoJaar, regels, vakanties)[mw.naam] || null;
    const urenDagen = wkUren?.dagen || {};

    // Spookdag: afwezig én tóch in een roostercel. Bewust over ALLE afwezige dagen en niet
    // alleen over de would-be-werkdagen hieronder: de urenTeller telde die 8u puur omdat de
    // naam in de cel staat, dus de aftrek hoort op diezelfde grondslag te staan. Zonder dat
    // zou Emre's zaterdag 29 aug (late_dienst_we, geen would-be-werkdag) blijven staan.
    for (const dag of alleAfwDagen) {
      if (staatInRoostercel(w.weekObj.rooster, mw.naam, dag)) afwezigCelDagen++;
    }

    const wbDagen = alleAfwDagen.filter(
      (dag) => wouldBeWerkdag(mw, dag, weekType, w.weeknummer, regels, vakDagen),
    );
    for (const dag of ALLE_DAGEN) {
      if (!wbDagen.includes(dag)) continue;
      const type = typeVanDag[dag] || 'anders';
      // Is deze dag al door een uren-record van hetzelfde type gedekt, dan is hij betaald en
      // hoort hij NIET nog eens 8u credit via het oude pad te krijgen. Valt hij af — geen
      // ingevuld getal, of een ander type dan de typeMap hem geeft — dan geldt onverkort
      // het oude pad.
      if (urenDagen[dag] === type) continue;
      wbTypeLijst.push({ type, uren: null });
    }
    // De weektotalen per type gaan ongeclipt naar de vaste stapel: Numbers heeft ze al
    // bepaald. Ze staan bewust LOS van de dagenlus hierboven — een weekdeel hoeft geen
    // would-be-werkdag te hebben om op te landen.
    for (const [t, u] of Object.entries(wkUren?.perType || {})) {
      wbTypeLijst.push({ type: t, uren: u });
    }
  }

  // ── Spookaftrek: uren die in Gepland zitten voor een dag die niet gewerkt is ─
  // week.urenTeller telt 8u per naam per roostercel en trekt afwezigheid NOOIT af
  // (analyseer-week.js:85, rooster-engine.js:739). Wie na het genereren verlof opneemt blijft
  // dus in zijn cel staan en houdt die 8u in Gepland. Op lade arnhem_eo waren dat op 25 aug
  // achttien dagen (144u) in week 33 t/m 40. Die uren horen niet in Gepland, dus gaan ze eraf.
  //
  // De teller is bewust in Gepland verwerkt en niet als extra afwezigheids-credit: het gaat om
  // uren die er nooit hadden mogen staan, niet om een tegoed. Het verschil is zichtbaar: als
  // credit zou zo'n dag TWEE keer meetellen (8u in Gepland én 8u credit = 16u effect), terwijl
  // hij nu precies één keer uit de telling verdwijnt en daarna via wbTypeLijst hooguit
  // normale credit terugkrijgt — nooit meer dan de blok-cap toestaat.
  //
  // ⚠️ ÉÉN OVERLAP MET markeringDelta, theoretisch. Op een spookdag met een celOverride telt
  // berekenMarkeringDelta (Weekrooster.jsx:75) normaal 0: getCel geeft dan aan BEIDE kanten
  // V/Z/A, want afwezigheid heeft daar voorrang (roostercodes.js:406-413). Die voorrang geldt
  // alleen op een would-be-werkdag; is de dag dat NIET, dan valt getCel door naar de dienst en
  // kan een 'leeg'-markering diezelfde 8u al hebben weggehaald — dan zou hier een tweede keer
  // afgetrokken worden. Vereist dus alle drie: spookdag + celOverride + geen would-be-werkdag.
  // Gemeten op de live-data van 25 aug: 0 keer (arnhem_eo heeft één override, week 31/Emre/di,
  // en dat is geen spookdag; maastricht heeft 0 spookdagen). Zie
  // test/diag/spookaftrek-blok9-10.mjs, dat dit per rij hertelt.
  const afwezigCelUren = UUR_PER_DIENST * afwezigCelDagen;
  const gepland = basis + markeringDelta - afwezigCelUren;

  // ── Afwezigheids-credit op blok-niveau ────────────────────────────────────
  // Twee stapels, want ze volgen verschillende regels:
  //   vasteUren  de handmatig ingevulde uren (verlof of ziekte) van dit blok. Die staan VAST —
  //              Numbers heeft ze al bepaald, dus er gaat geen aftopping overheen.
  //   restTypes  alle overige afwezige would-be-werkdagen ('anders', en verlof of ziekte
  //              zonder ingevuld getal). Die houden het oude pad: 8u per dag, afgetopt op
  //              wat er van het blok-gat NA de vaste uren nog over is.
  let vasteUren = 0;
  const vastePerType = {};   // dezelfde vaste uren, opgeteld per type van de dag
  const restTypes = [];
  for (const p of wbTypeLijst) {
    if (p.uren === null) {
      restTypes.push(p.type);
    } else {
      vasteUren += p.uren;
      vastePerType[p.type] = (vastePerType[p.type] || 0) + p.uren;
    }
  }
  const blokGap = Math.max(0, target - gepland - vasteUren);
  let credit = Math.max(0, Math.min(blokGap, UUR_PER_DIENST * restTypes.length));
  const afwUren = vasteUren + credit;
  const afwPerType = { verlof: 0, ziekte: 0, anders: 0 };
  // De vaste uren gaan naar de bak van het type van hun eigen dag (p.type — dezelfde
  // typeMap waarop de resttypes hieronder boeken), zodat elk uur in de uitsplitsing het
  // label van zijn dag draagt. Hiermee blijft SUM(afwPerType) === afwUren: de vaste uren
  // landen hier per type en zijn samen precies vasteUren, en de lus hieronder deelt precies
  // `credit` uit — samen exact afwUren.
  for (const [t, uren] of Object.entries(vastePerType)) {
    afwPerType[t] = (afwPerType[t] || 0) + uren;
  }
  for (const t of restTypes) {
    if (credit <= 0) break;
    const deel = Math.min(UUR_PER_DIENST, credit);
    afwPerType[t] = (afwPerType[t] || 0) + deel;
    credit -= deel;
  }

  return {
    target,
    gepland,
    afwUren,
    afwPerType,
    afwijking: gepland + afwUren - target,
    ontbrekendeWeken,
    afwezigCelUren,
  };
}
